import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Cropper from "react-easy-crop";
import { Point, Area } from "react-easy-crop/types";

import {
  Grid,
  Button,
  IconButton,
  Typography,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faPencil, faImage } from "@fortawesome/free-solid-svg-icons";

import getCroppedImgAsDataUrl from "./utils/cropImage";
import { createImage, readFileAsDataUrl } from "./utils/cropImage";
import { dataURLtoBlob } from "./utils/dataURLtoBlob";

import { makeBlobUrl } from "../../services/documents/document-files-api.service";

import { Props } from "./interfaces";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  InitialImgContainer,
  CropperContainer,
  DialogActions,
  DialogActionsDivider,
  EditUtilsContainer,
  UtilsButtonsContainer,
  UtilsButton,
  SaveImageButton,
} from "./styles";

const ImageCropper = ({
  openButtonIcon,
  openButtonText,
  propImageBlob,
  handleSaveImage,
  title,
  roundCropShape,
  customButtonStyle,
}: Props) => {
  const { t } = useTranslation();
  const tRP = "components.cropper"; // tRP - translation root path
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [imageBlobUrl, setImageBlobUrl] = useState<string | null>(null);
  const [originalBlobUrl, setOriginalBlobUrl] = useState<string | null>(null);
  const [fileBlobUrl, setFileBlobUrl] = useState<string | null>(null);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [aspect, setAspect] = useState<number>(1 / 1);
  const [useOriginal, setUseOriginal] = useState<boolean>(false);
  const [fileLoaded, setFileLoaded] = useState<boolean>(false);

  useEffect(() => {
    if (propImageBlob) {
      const imageUrl = makeBlobUrl(propImageBlob);
      setImageBlobUrl(imageUrl);
      setOriginalBlobUrl(imageUrl);
      createImage(imageUrl).then((image) => setCroppedArea({ width: image.width, height: image.height, x: 0, y: 0 }));
    }
  }, [propImageBlob]);

  const onCropComplete = (_: any, croppedAreaPixels: Area) => {
    if (zoom < 1) {
      setCrop({
        x: 0,
        y: 0,
      });
    }

    setCroppedArea({
      ...croppedAreaPixels,
      x: zoom < 1 ? 0 : croppedAreaPixels.x,
      y: zoom < 1 ? 0 : croppedAreaPixels.y,
    });
  };

  const onFileChange = async (files: FileList) => {
    if (files && files.length > 0) {
      let imageDataURL = await readFileAsDataUrl(files[0]);

      setImageBlobUrl(imageDataURL as string);
      setFileBlobUrl(imageDataURL as string);
      setEditMode(true);
      setFileLoaded(true);
    }
  };

  const resetState = () => {
    setZoom(1);
    setRotation(0);
    setAspect(1 / 1);
    setCroppedArea(null);
    setUseOriginal(false);
    setFileLoaded(false);
    setImageBlobUrl(originalBlobUrl);
    setFileBlobUrl(null);
    setEditMode(false);
  };

  const handleClose = () => {
    if (editMode) {
      setEditMode(false);
    } else {
      setDialogOpen(false);
    }

    resetState();
  };

  const saveImage = async () => {
    try {
      const croppedImgDataUrl = await getCroppedImgAsDataUrl(imageBlobUrl, croppedArea, rotation, zoom, aspect);

      await handleSaveImage(dataURLtoBlob(useOriginal && fileLoaded ? fileBlobUrl : croppedImgDataUrl));
      setDialogOpen(false);
      resetState();
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <>
      {openButtonIcon ? (
        <IconButton
          style={{ ...customButtonStyle }}
          onClick={() => setDialogOpen(true)}
        >
          {openButtonIcon}
        </IconButton>
      ) : (
        <Button
          onClick={() => setDialogOpen(true)}
          style={{ ...customButtonStyle }}
        >
          {openButtonText ?? t(`${tRP}.open_button_default_text`)}
        </Button>
      )}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        fullWidth
        editMode={editMode}
      >
        <DialogTitle editMode={editMode}>
          <Typography
            variant="h5"
            component="span"
          >
            {t(`${tRP}.${editMode ? "edit_mode" : "default"}_dialog_title`)}
          </Typography>
          <Typography
            variant="h6"
            component="span"
          >
            {title}
          </Typography>
          <IconButton onClick={handleClose}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!editMode ? (
            propImageBlob && (
              <InitialImgContainer bgImage={makeBlobUrl(propImageBlob)}>
                {!propImageBlob && <Typography>{t(`${tRP}.no_image_placeholder`)}</Typography>}
              </InitialImgContainer>
            )
          ) : useOriginal ? (
            imageBlobUrl && (
              <InitialImgContainer bgImage={imageBlobUrl}>
                {!propImageBlob && <Typography>{t(`${tRP}.no_image_placeholder`)}</Typography>}
              </InitialImgContainer>
            )
          ) : (
            <CropperContainer>
              <Cropper
                image={imageBlobUrl ?? ""}
                crop={crop}
                cropShape={roundCropShape ? "round" : "rect"}
                rotation={rotation}
                zoom={zoom}
                aspect={aspect}
                onCropChange={setCrop}
                onRotationChange={setRotation}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                zoomSpeed={0.5}
              />
            </CropperContainer>
          )}
        </DialogContent>
        {editMode && fileLoaded && (
          <Grid>
            <InputLabel>{t(`${tRP}` + (useOriginal ? ".use_original" : ".use_cropped"))}</InputLabel>
            <Switch
              checked={useOriginal}
              onClick={() => setUseOriginal(!useOriginal)}
              disabled={!fileLoaded}
            />
          </Grid>
        )}
        <DialogActions>
          {editMode && (
            <EditUtilsContainer>
              <Grid>
                <Typography>{t(`${tRP}.zoom_slider_label`)}</Typography>
                <Typography>{zoom}</Typography>
                <Slider
                  value={zoom}
                  onChange={(_, value) => setZoom(value as number)}
                  step={0.1}
                  min={0.1}
                  max={3}
                />
              </Grid>
              <FormControl>
                <InputLabel>{t(`${tRP}.aspect_select_label`)}</InputLabel>
                <Select
                  value={aspect}
                  label="Aspect"
                  onChange={({ target }) => setAspect(target.value as number)}
                >
                  <MenuItem value={4 / 3}>4:3</MenuItem>
                  <MenuItem value={16 / 9}>16:9</MenuItem>
                  <MenuItem value={1 / 1}>1:1</MenuItem>
                </Select>
              </FormControl>
              <Grid>
                <Typography>{t(`${tRP}.rotation_slider_label`)}</Typography>
                <Typography>{rotation}</Typography>
                <Slider
                  // color="secondary"
                  value={rotation}
                  onChange={(_, value) => setRotation(value as number)}
                  step={5}
                  min={-180}
                  max={180}
                  style={{
                    color: theme.palette.actions.cancel,
                    // border: theme.palette.actions.cancel
                  }}
                />
              </Grid>
            </EditUtilsContainer>
          )}
          <DialogActionsDivider />
          {!editMode && (
            <UtilsButtonsContainer>
              <Grid>
                {propImageBlob && (
                  <UtilsButton onClick={() => setEditMode(true)}>
                    <FontAwesomeIcon icon={faPencil} />
                    <Typography>{t("buttons.edit")}</Typography>
                  </UtilsButton>
                )}
                <UtilsButton component="label">
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={({ target: { files } }) => files && files.length > 0 && onFileChange(files)}
                  />
                  <FontAwesomeIcon icon={faImage} />
                  <Typography>{t("buttons.add")}</Typography>
                </UtilsButton>
              </Grid>
              {/* <UtilsButton>
                <FontAwesomeIcon icon={faTrashCan} />
                <Typography>{t("buttons.delete")}</Typography>
              </UtilsButton> */}
            </UtilsButtonsContainer>
          )}
          {editMode && (
            <SaveImageButton
              variant="contained"
              onClick={saveImage}
            >
              {t(`${tRP}.save_image_button`)}
            </SaveImageButton>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImageCropper;
